import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { deleteMerchantSuggestion, getTransaction, listMerchantSuggestionApplicableTransactions, onSubmitFeedback, } from 'actions';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, Button, Indicator, rem } from 'design-system';
import { goBackWithKey } from 'utils/navigationv6';
import { useMarginBottom } from 'hooks/useMarginBottom';
import SectionHeader from 'design-system/SectionHeader';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import EditMultipleListRow from '../../components/multiple/EditMutipleListRow';
import { makeSelectTransactionById } from '../../selectors';
import TransactionLogo from '../../components/TransactionLogo';
import { isWeb } from '../../../../constants';
function nTransactions(n) {
    return n > 1 ? `${n} transactions` : '1 transaction';
}
const strings = {
    updateTitle: 'Update logo',
    deleteTitle: 'Delete edit',
    btnApply: (count) => `Apply edit to ${nTransactions(count)}`,
    btnDelete: (count) => `Delete edit from ${nTransactions(count)}`,
    similarSectionTitle: 'Similar transactions',
    deleteNote: 'This will revert the logo for the transactions below back to the original',
};
function keyExtractor(item) {
    return String(item.id);
}
const ListHeader = React.memo(({ suggestedData, transaction }) => (<View>
    {suggestedData ? (<View style={styles.merchantLogoContainer}>
        <TransactionLogo transaction={{ suggestedData }} size={56}/>
      </View>) : (<ScreenInfoHeader subTitle={strings.deleteNote}/>)}
    <EditMultipleListRow readonly item={transaction} isFirstItem isLastItem/>
  </View>));
const PreviewMerchantOverrideView = React.memo(({ newMerchantData, transaction, isFetchingOthers, otherTransactions, otherTransactionsTotalCount, suggestedData, onEndReached, onPressConfirm, isConfirming, }) => {
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const listStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const totalCount = otherTransactionsTotalCount === undefined
        ? undefined
        : otherTransactionsTotalCount + 1;
    const suggestionToDelete = !newMerchantData
        ? suggestedData
        : null;
    const renderItem = useCallback(({ item, index }) => (<EditMultipleListRow readonly item={!suggestionToDelete
            ? item
            : { ...item, suggestedData: suggestionToDelete }} isFirstItem={index === 0} isLastItem={index + 1 === otherTransactions.length}/>), [suggestionToDelete, otherTransactions.length]);
    const ListHeaderComponent = useMemo(() => (<>
          <ListHeader suggestedData={newMerchantData} transaction={transaction}/>
          {!!otherTransactionsTotalCount && (<SectionHeader horizontalMargins style={styles.sectionHeader}>
              {strings.similarSectionTitle}
            </SectionHeader>)}
        </>), [newMerchantData, otherTransactionsTotalCount, transaction.id]
    // do not re-render if transaction's merchant data changes
    );
    const ListFooterComponent = useMemo(() => (<View style={styles.indicatorContainer}>
          <Indicator dark/>
        </View>), []);
    return (<View style={styles.container}>
        <FlatList renderItem={renderItem} data={otherTransactions} keyExtractor={keyExtractor} ListHeaderComponent={ListHeaderComponent} ListFooterComponent={isFetchingOthers ? ListFooterComponent : null} contentContainerStyle={listStyle} onEndReached={onEndReached} scrollEventThrottle={16} onEndReachedThreshold={0.8} showsVerticalScrollIndicator/>
        <Button floating brand title={(newMerchantData ? strings.btnApply : strings.btnDelete)(totalCount ?? 1)} isFetching={isFetchingOthers || isConfirming} onPress={() => {
            if (totalCount)
                onPressConfirm(totalCount);
        }}/>
      </View>);
});
const PreviewMerchantOverrideScreen = ({ navigation, route }) => {
    const { keyBack, merchantData: newMerchantData, transactionId, } = route.params ?? {};
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            title: newMerchantData === null ? strings.deleteTitle : strings.updateTitle,
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary, newMerchantData]);
    const goBack = useCallback(() => {
        if (keyBack) {
            const ok = goBackWithKey(navigation, keyBack);
            if (ok)
                return;
        }
        if (navigation.canGoBack()) {
            navigation.goBack();
            return;
        }
        navigation.navigate('Feed');
    }, [keyBack]);
    const selectTransactionById = useMemo(makeSelectTransactionById, []);
    const transaction = useAppSelector((store) => selectTransactionById(store, transactionId));
    useEffect(() => {
        if (!transaction || newMerchantData === undefined)
            goBack();
    }, [goBack, newMerchantData, transaction]);
    const [nextPage, setNextPage] = useState(1);
    const [perPage, setPerPage] = useState(undefined);
    const [totalCount, setTotalCount] = useState(undefined);
    const [otherTransactions, setOtherTransactions] = useState([]);
    const [currSuggestedData, setCurrSuggestedData] = useState(transaction?.suggestedData ?? null);
    const [, fetchError, isFetching, fetch] = useFetchOld({
        throws: false,
        muteResult: true,
    });
    const [, , isSubmitting, submit] = useFetchOld(muteErrorAndResult);
    const dispatch = useAppDispatch();
    const fetchNextPageImpl = useCallback((transactionId, fetchError = undefined, isFetching = false, nextPage = 1, perPage = undefined, totalCount = undefined) => {
        if (isFetching || fetchError !== undefined) {
            return;
        }
        if (totalCount !== undefined &&
            perPage !== undefined &&
            nextPage * perPage >= totalCount) {
            return;
        }
        fetch(listMerchantSuggestionApplicableTransactions(transactionId, {
            page: nextPage,
            perPage,
        })).then((result) => {
            if (!result)
                return;
            const { transactions, paging, suggestedData } = result;
            setCurrSuggestedData((s) => s || suggestedData);
            setPerPage(paging.perPage);
            setTotalCount(paging.totalCount);
            if (transactions.length > 0) {
                setOtherTransactions((ts) => {
                    const index = (paging.page - 1) * paging.perPage;
                    let out = ts.slice(0, index);
                    if (out.length > 0) {
                        const idSet = new Set(out.map((t) => t.id));
                        out = out.concat(transactions.filter((t) => !idSet.has(t.id)));
                    }
                    else {
                        out = transactions;
                    }
                    return out;
                });
            }
            if (paging.page < paging.pageCount) {
                setNextPage(paging.page + 1);
            }
        });
    }, []);
    const fetchNextPage = useCallback(() => {
        if (!transactionId)
            return;
        fetchNextPageImpl(transactionId, fetchError, isFetching, nextPage, perPage, totalCount);
    }, [
        fetchNextPageImpl,
        fetchError,
        isFetching,
        nextPage,
        perPage,
        totalCount,
        transactionId,
    ]);
    useEffect(() => {
        if (!transactionId)
            return;
        fetchNextPageImpl(transactionId);
    }, [fetchNextPageImpl, transactionId]);
    const confirm = useCallback((editCount) => {
        if (!transactionId)
            return;
        submit(newMerchantData
            ? onSubmitFeedback(transactionId, newMerchantData.type, newMerchantData.handle)
            : deleteMerchantSuggestion(transactionId)).then(() => {
            dispatch(getTransaction(transactionId)); // don't wait
            navigation.navigate('LearningScreen', {
                keyBack: keyBack ?? '',
                transactionsCount: editCount,
                editData: {
                    editType: 'merchantLogo',
                    isDelete: newMerchantData === null,
                },
            });
        });
    }, [keyBack, newMerchantData, submit, transactionId]);
    if (!transaction || newMerchantData === undefined)
        return null;
    return (<AppView withFrame={false} avoidHeader>
        <PreviewMerchantOverrideView transaction={transaction} suggestedData={currSuggestedData} newMerchantData={newMerchantData} isFetchingOthers={isFetching} otherTransactions={otherTransactions} otherTransactionsTotalCount={totalCount} onEndReached={fetchNextPage} onPressConfirm={confirm} isConfirming={isSubmitting}/>
      </AppView>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    sectionHeader: {
        marginTop: rem(24),
        marginBottom: rem(16),
    },
    indicatorContainer: {
        marginTop: rem(16),
    },
    merchantLogoContainer: {
        marginTop: rem(16),
        marginBottom: rem(24),
        alignItems: 'center',
    },
});
export default PreviewMerchantOverrideScreen;
